import React, { useState } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Box,
  SvgIcon,
  GlobalStyles,
} from "@mui/material";
import {
  LocalizationProvider,
  DateRangeCalendar,
} from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LuCalendar } from "@/Components/Icons/Icons";
import dayjs from "dayjs";
// import FormLabel from "./FormLabel";

export default function DateRangeSelector({
  value: externalValue,
  onChange,
  sx = {},
  textFieldSx = {},
  width = "20%",
  height,
  inputSize = "small",
  disabled,
  gap,
  maxDateToToday = false, // 👈 new prop
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  // Format displayed value with placeholder
  // const defaultStart = dayjs();
  // const defaultEnd = maxDateToToday ? dayjs().add(-7, "day") : dayjs().add(7, "day");

  const defaultEnd = maxDateToToday ? dayjs() : dayjs().add(7, "day");
  const defaultStart = maxDateToToday ? dayjs().add(-7, "day") : dayjs();
  
  const [value, setValue] = useState([defaultStart, defaultEnd]);

  const currentValue = externalValue || value;

  // Format displayed value
  const formatted = (() => {
    const [start, end] = currentValue || [];
    const startText = start
      ? dayjs(start).format("YYYY-MM-DD")
      : defaultStart.format("YYYY-MM-DD");
    const endText = end
      ? dayjs(end).format("YYYY-MM-DD")
      : defaultEnd.format("YYYY-MM-DD");
    return `${startText} - ${endText}`;
  })();

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleDateChange = (newValue) => {
    if (onChange) onChange(newValue);
    else setValue(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <>
        {" "}
        <GlobalStyles
          styles={{
            ".MuiYearCalendar-root": {
              display: "flex !important",
              flexDirection: "column !important",
              alignItems: "center !important",
              overflowY: "auto !important",
              maxHeight: "250px !important",
              paddingRight: "8px !important",
              width: "auto !important", // 👈 remove fixed 320px
              columnGap: "0 !important", // 👈 remove gap
              rowGap: "0 !important", // 👈 remove row gap
              flexWrap: "nowrap !important", // 👈 disable wrapping
            },
            ".MuiMonthCalendar-root": {
              display: "flex !important",
              flexDirection: "column !important",
              alignItems: "center !important",
              overflowY: "auto !important",
              maxHeight: "250px !important",
              paddingRight: "8px !important",
              width: "auto !important", // 👈 remove fixed 320px
              columnGap: "0 !important", // 👈 remove gap
              rowGap: "0 !important", // 👈 remove row gap
              flexWrap: "nowrap !important", // 👈 disable wrapping
            },
            ".MuiYearCalendar-root .MuiPickersYear-root": {
              width: "80px !important", // 👈 fixed smaller width
              textAlign: "center !important",
              padding: "6px 0 !important",
              borderRadius: "6px !important",
              margin: "2px auto !important", // 👈 center horizontally
            },
          }}
        />
        <Box
          display="flex"
          flexDirection="column"
          // gap={label ? 1 : 0}
          width={width}
          height={height}
          sx={{ ...sx }}
        >
          {/* <FormLabel label={label} gap={gap} /> */}

          <Box onClick={handleOpen}>
            <TextField
              disabled={disabled}
              value={formatted}
              fullWidth
              readOnly
              size={inputSize}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton tabIndex={-1}>
                      <SvgIcon sx={{ color: "var(--gray-600)" }}>
                        <LuCalendar color="var(--brand-600)" />
                      </SvgIcon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "16px",
                  fontWeight: 400,
                  borderRadius: "8px",
                  bgcolor: disabled ? "var(--gray-800)" : "transparent",
                  cursor: "pointer",
                  height: height || 40,
                  alignItems: "center",
                  "& fieldset": {
                    border: "1px solid var(--brand-600)",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--gray-300)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--gray-300)",
                  },
                  ...(Boolean(anchorEl) && {
                    borderColor: "var(--brand-600) !important",
                    boxShadow: "0 0 0 4px var(--brand-200)",
                    "& fieldset": {
                      border: "1px solid var(--brand-600) !important",
                    },
                  }),
                },
                "& .MuiInputBase-input": {
                  padding: "12px 14px",
                  cursor: "pointer",
                  color: "var(--brand-800)", // 👈 dynamic color
                },
                ...textFieldSx,
              }}
            />
          </Box>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
              sx: {
                border: "1px solid var(--gray-300)",
                borderRadius: "8px",
                boxShadow: "0px 4px 12px var(--gray-300)",
              },
            }}
          >
            <Box>
              <DateRangeCalendar
                disabled={disabled}
                value={currentValue}
                onChange={(newValue, selectionState) => {
                  handleDateChange(newValue);
                  if (selectionState === "finish") handleClose();
                }}
                calendars={1}
                views={["year", "month", "day"]}
                minDate={dayjs("2000-01-01")}
                maxDate={maxDateToToday ? dayjs() : dayjs().endOf("year")} // 👈 conditionally limit
                shouldDisableYear={(year) =>
                  year.year() < 2000 || year.year() > dayjs().year()
                }
                slotProps={{
                  day: {
                    sx: {
                      "&.Mui-selected": {
                        backgroundColor: "var(--brand-700)",
                        color: "var(--gray-0)",
                        "&:hover": {
                          backgroundColor: "var(--brand-700)",
                        },
                      },
                    },
                  },
                  calendarHeader: {
                    sx: {
                      borderBottom: "1px solid var(--gray-300)",
                      px: 2,
                      pt: 1.5,
                      pb: 1.5,
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      "& .MuiPickersCalendarHeader-labelContainer": {
                        display: "block",
                        textAlign: "center",
                        position: "absolute",
                      },
                      "& .MuiPickersCalendarHeader-switchViewButton": {
                        display: "none",
                      },
                      "& .MuiPickersArrowSwitcher-root": {
                        position: "absolute",
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        pointerEvents: "none",
                      },
                      "& .MuiIconButton-root": {
                        pointerEvents: "auto",
                        zIndex: 1,
                      },
                    },
                  },
                }}
                sx={{ width: "100%" }}
              />
            </Box>
          </Popover>
        </Box>
      </>
    </LocalizationProvider>
  );
}
